import * as Moment from "moment";
import { IDateDuration } from "./IDateDuration";

export class DateDuration {
  static today(): DateDuration {
    const now = new Date();
    return new DateDuration(now, now);
  }

  /**
   * 計算式で当日も含む場合にshouldContainCurrentがtrueになる
   */
  static fromTo(from: Date, to: Date, shouldContainCurrent: boolean): DateDuration {
    return new DateDuration(from, to, shouldContainCurrent);
  }

  static fromDays(from: Date, days: number): DateDuration {
    return DateDuration.createDateDuration(from, Moment(from).add(days, "days").toDate());
  }

  static fromMonths(from: Date, months: number): DateDuration {
    return DateDuration.createDateDuration(from, Moment(from).add(months, "months").toDate());
  }

  private static createDateDuration(a: Date, b: Date): DateDuration {
    return (a <= b)
      ? new DateDuration(a, b)
      : new DateDuration(b, a);
  }

  static areEqual(a: DateDuration, b: DateDuration) {
    return (a === b)
        || ((a && b)
         && DateDuration.areDatesEqual(a.from, b.from)
         && DateDuration.areDatesEqual(a.to, b.to));
  }

  private static areDatesEqual(a: Date, b: Date): boolean {
    return (a === b)
        || ((a && b)
         && (a.getTime() === b.getTime()));
  }

  constructor(a: Date, b: Date, shouldContainCurrent: boolean = false) {
    this._from = this.createNewDate(a);
    this._to = this.createNewDate(b);
    this._shouldContainCurrent = shouldContainCurrent;
  }

  private createNewDate(source: Date): Date {
    return !source
      ? null
      : new Date(
        source.getFullYear(),
        source.getMonth(),
        source.getDate(),
        0,
        0,
        0,
        0
      );
  }

  private _from: Date;
  private _to: Date;

  /**
   * 計算式でのみ使用している
   */
  private _shouldContainCurrent: boolean = false;

  get shouldContainCurrent(): boolean {
    return this._shouldContainCurrent;
  }

  get from(): Date {
    return this._from;
  }

  set from(value: Date) {
    this._from = this.createNewDate(value);
  }

  get to(): Date {
    return this._to;
  }

  set to(value: Date) {
    this._to = this.createNewDate(value);
  }

  get years(): number {
    return Math.floor(this.months / 12);
  }

  get months(): number {
    // TODO:計算式で使用するときには_shouldContainCurrentの対応が必要
    return DateDuration.readMonths(this.to) - DateDuration.readMonths(this.from)
      - ((this.to.getDate() < this.from.getDate()) ? 1 : 0);
  }

  get days(): number {
    const additional = this._shouldContainCurrent ? 1 : 0;
    return DateDuration.readDays(this.to) - DateDuration.readDays(this.from) + additional;
  }

  get weeks(): number {
    return Math.floor(this.days / 7);
  }

  get isValid(): boolean {
    return !this.from
        || !this.to
        || this.from <= this.to;
  }

  private static readMonths(source: Date): number {
    return source.getFullYear() * 12 + source.getMonth();
  }

  private static readDays(source: Date): number {
    return Math.floor(source.getTime() / (1000 * 60 * 60 * 24));
  }

  toString(format: string): string {
    // 年齢表示の時、V1と差異が出てしまうので合わせこむ為に暫定対応している
    if (format.indexOf("歳") >= 0) {
      return this.toAgeString(format);
    }
    return format
      .replace("w", this.weeks.toString())
      .replace("wd", (this.days - this.weeks * 7).toString())
      .replace("y", this.years.toString())
      .replace("M", (this.months - this.years * 12).toString())
      .replace("d", ((new DateDuration(this.from, Moment(this.to).add(- this.months, "months").toDate())).days).toString());
  }

  /**
   * どうしても日数が合わないので、V1の実装を移植した。
   * http://git.pscad.co.jp:8080/gitbucket/documaker/DocuMakerV1.5/blob/develop/src/findex/documaker/utility/ChildAge.java
   */
  toAgeString(format: string): string {
    let yearAge = 0;
    let monthAge = 0;
    let dayAge = 0;
    
    const birthYear = this.from.getFullYear();
    const dateYear = this.to.getFullYear();
    const birthMonth = this.from.getMonth() + 1;
    const dateMonth = this.to.getMonth() + 1;
    const birthDay = this.from.getDate();
    const dateDay = this.to.getDate();

    // 年
    yearAge = dateYear - birthYear;

    // 月
    let plusMonth = 0;
    if (birthMonth > dateMonth) {
      yearAge--;
      plusMonth = 12;
    }
    monthAge = dateMonth - birthMonth + plusMonth;

    // 日
    const thisLastDay = new Date(this.to.getFullYear(), this.to.getMonth() + 1, 0).getDate(); // 当月最終日
    const beforelastDay = new Date(this.to.getFullYear(), this.to.getMonth(), 0).getDate(); // 前月最終日

    // 最終日
    if (thisLastDay === dateDay) {
      // 最終日が当月にない
      if (thisLastDay <= birthDay) {
        dayAge = 0;

      } else {
        // 日付がまだ来ていない
        if (dateDay < birthDay) {
          monthAge--;
          if (monthAge < 0) {
            monthAge = 11;
            yearAge--;
          }

          // 前月の差分
          let diff = birthDay - beforelastDay;
          if (diff < 0) { diff = 0; }

          dayAge = beforelastDay - birthDay + dateDay + diff;

        } else {
          dayAge = dateDay - birthDay;
        }
      }

      // 通常日
    } else {
      // 日が同じ
      if (birthDay === dateDay) {
        dayAge = 0;

      } else {
        // 日付がまだ来ていない
        if (dateDay < birthDay) {
          monthAge--;
          if (monthAge < 0) {
            monthAge = 11;
            yearAge--;
          }

          // 前月の差分
          let diff = birthDay - beforelastDay;
          if (diff < 0) { diff = 0; }

          dayAge = beforelastDay - birthDay + dateDay + diff;

        } else {
          dayAge = dateDay - birthDay;
        }
      }
    }

    // 生まれた年月(0)の時は1日進める
    if (yearAge === 0 && monthAge === 0) { dayAge++; }

    return format
      .replace("w", this.weeks.toString())                    // 年齢計算ではwのフォーマット指定は無い筈
      .replace("wd", (this.days - this.weeks * 7).toString()) // 年齢計算ではwdのフォーマット指定は無い筈
      .replace("y", yearAge.toString())
      .replace("M", monthAge.toString())
      .replace("d", dayAge.toString());
  }

  clone(): DateDuration {
    return new DateDuration(
      this.from,
      this.to
    );
  }

  static from(source: IDateDuration): DateDuration {
    if (!source) {
      return null;
    }
    return new DateDuration(source.from, source.to, source.shouldContainCurrent);
  }

  static to(source: DateDuration): IDateDuration {
    if (!source) {
      return null;
    }
    return {
      from: source.from,
      to: source.to,
      shouldContainCurrent: source.shouldContainCurrent,
    };
  }
}
